import styled from '@emotion/styled';
import { Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@landsbankinn/ui';
import type { TableProps } from '@landsbankinn/ui/dist/components/table/Table';
import { find } from 'lodash';
import Link from 'next/link';
import React from 'react';
import type { Column, Row } from 'react-table';
import { useSortBy, useTable } from 'react-table';
import { OrderByArrow } from '../markets/shared/OrderByArrow';

// eslint-disable-next-line @typescript-eslint/ban-types
interface IProps<T extends object> extends TableProps {
  columns: ReadonlyArray<Column<T>>;
  data?: T[];
  rowLink?: (t: Row<T>) => { href: string; as: string };
  initialState?: { sortBy: { id: string; desc: boolean }[] };
  firstColumnTitle?: boolean;
}

export const DataTableTh = styled(Th)`
  white-space: nowrap;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;
`;

const ArrowContainer = styled.div<{ display: boolean }>`
  visibility: ${(props) => (props.display ? 'visible' : 'hidden')};
  padding-right: 2px;
  padding-left: 2px;
  padding-bottom: 11px;
`;

// eslint-disable-next-line @typescript-eslint/ban-types
export function DataTable<T extends object>({
  columns,
  data = [],
  rowLink,
  initialState,
  firstColumnTitle = true,
  ...rest
}: IProps<T>) {
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    prepareRow,
    rows,
    footerGroups,
  } = useTable<T>(
    {
      columns,
      data,
      initialState: initialState,
      disableSortRemove: true,
    },
    useSortBy,
  );

  const hasFooter = find(columns, (i) => i.Footer);

  return (
    <Table {...rest} {...getTableProps()}>
      <Thead>
        {headerGroups.map((headerGroup) => (
          <Tr
            {...headerGroup.getHeaderGroupProps()}
            firstColumnTitle={firstColumnTitle}
            key={`${headerGroup.id}`}
          >
            {headerGroup.headers.map((header) => (
              <DataTableTh key={header.id}>
                <HeaderContainer
                  {...header.getHeaderProps([
                    {
                      style: (header as any).headerStyle,
                    },
                    header.getSortByToggleProps(),
                  ])}
                >
                  <ArrowContainer display={header.isSorted ?? false}>
                    <OrderByArrow desc={header.isSortedDesc} />
                  </ArrowContainer>
                  <div>{header.render('Header')}</div>
                </HeaderContainer>
              </DataTableTh>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          const r = (
            <Tr {...row.getRowProps()} key={row.getRowProps().key}>
              {row.cells.map((cell, i) => {
                return (
                  <Td
                    {...cell.getCellProps([
                      {
                        style: (cell as any).column.style,
                      },
                    ])}
                    key={cell.getCellProps().key}
                  >
                    {cell.render('Cell')}
                  </Td>
                );
              })}
            </Tr>
          );
          if (rowLink) {
            return <Link {...rowLink(row)}>{r}</Link>;
          }
          return r;
        })}
      </Tbody>
      {hasFooter !== undefined && rows.length > 1 && footerGroups.length > 0 && (
        <Tfoot>
          {footerGroups.map((group) => (
            <Tr
              {...group.getFooterGroupProps()}
              key={group.getFooterGroupProps().key}
            >
              {group.headers.map((column) => (
                <Td
                  {...column.getFooterProps([
                    {
                      style: (column as any).style,
                    },
                  ])}
                  key={column.getFooterProps().key}
                >
                  {column.render('Footer')}
                </Td>
              ))}
            </Tr>
          ))}
        </Tfoot>
      )}
    </Table>
  );
}
